import { useEffect, useRef, useState } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { api } from '../api/client';
import { ApiError, PushEventsSettings, SettingKey } from '../api/types';
import { theme } from '../theme';
import { Icon } from '../components/Icon';
import { InvitePanel, copyToClipboard } from '../components/InvitePanel';
import { fmtMoney } from '../utils/format';
import { computeDailyBudget } from '../utils/daily-budget';
import {
  deviceLabel,
  getCurrentSubscription,
  isStandalone,
  pushSupported,
  subscribePush,
  unsubscribePush,
} from '../utils/push';

interface Props {
  onOpenCategories: () => void;
  onClose: () => void;
}

export function Settings({ onOpenCategories, onClose }: Props) {
  const { viewData, refresh } = useBudget();
  const toast = useToast();

  const settings = viewData?.settings;
  const currency = settings?.currency ?? '₽';
  const dailyBudget = viewData ? computeDailyBudget(viewData.categories, viewData.settings) : 0;
  const categoriesCount = viewData?.categories.length ?? 0;

  const [savingsStr, setSavingsStr] = useState(String(settings?.savings_target ?? 30));
  const [currencyStr, setCurrencyStr] = useState(currency);
  const [overrideStr, setOverrideStr] = useState(
    settings?.daily_budget_override != null ? String(settings.daily_budget_override) : '',
  );
  const [statsSinceStr, setStatsSinceStr] = useState(settings?.stats_since ?? '');
  const [busy, setBusy] = useState<SettingKey | 'logout' | null>(null);

  async function persist(key: SettingKey, value: string | number | null, ok: string) {
    setBusy(key);
    try {
      await api.setSetting(key, value);
      await refresh();
      toast.show(ok);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ padding: '26px 20px 100px' }}>
      <Header onClose={onClose} />

      <Section title="Финансы">
        <Row label="Целевая норма сбережений">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              inputMode="decimal"
              value={savingsStr}
              onChange={(e) => setSavingsStr(e.target.value)}
              style={inputStyle}
            />
            <button
              disabled={busy === 'savings_target'}
              onClick={() => {
                const n = Number(savingsStr);
                if (!Number.isFinite(n) || n < 0) {
                  toast.show('число от 0', 'error');
                  return;
                }
                persist('savings_target', n, 'Норма обновлена');
              }}
              style={btnStyle}
            >
              <Icon name="check" size={15} />
            </button>
          </div>
        </Row>

        <Row label="Валюта">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              maxLength={4}
              value={currencyStr}
              onChange={(e) => setCurrencyStr(e.target.value)}
              style={inputStyle}
            />
            <button
              disabled={busy === 'currency'}
              onClick={() => {
                const v = currencyStr.trim();
                if (!v) {
                  toast.show('пустая валюта', 'error');
                  return;
                }
                persist('currency', v, 'Валюта обновлена');
              }}
              style={btnStyle}
            >
              <Icon name="check" size={15} />
            </button>
          </div>
        </Row>

        <Row label="Дневной лимит (override)">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number"
              inputMode="decimal"
              value={overrideStr}
              onChange={(e) => setOverrideStr(e.target.value)}
              placeholder={`по умолчанию ${fmtMoney(dailyBudget, currency)}`}
              style={inputStyle}
            />
            <button
              disabled={busy === 'daily_budget_override'}
              onClick={() => {
                const trimmed = overrideStr.trim();
                if (!trimmed) {
                  persist('daily_budget_override', null, 'Override снят');
                  return;
                }
                const n = Number(trimmed);
                if (!Number.isFinite(n) || n <= 0) {
                  toast.show('число > 0', 'error');
                  return;
                }
                persist('daily_budget_override', n, 'Override сохранён');
              }}
              style={btnStyle}
            >
              <Icon name="check" size={15} />
            </button>
          </div>
          <div style={{ fontSize: 11, color: theme.colors.sub, marginTop: 4 }}>
            пусто → по планам повседневных категорий ({fmtMoney(dailyBudget, currency)})
          </div>
        </Row>

        <Row label="Статистика с даты">
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="date"
              value={statsSinceStr}
              onChange={(e) => setStatsSinceStr(e.target.value)}
              style={inputStyle}
            />
            <button
              disabled={busy === 'stats_since'}
              onClick={() => {
                const v = statsSinceStr.trim();
                if (!v) {
                  persist('stats_since', null, 'Граница снята — статистика по всей истории');
                  return;
                }
                persist('stats_since', v, 'Статистика теперь считается с этой даты');
              }}
              style={btnStyle}
            >
              <Icon name="check" size={15} />
            </button>
          </div>
          <div style={{ fontSize: 11, color: theme.colors.sub, marginTop: 4 }}>
            операции до даты не входят в дашборд (импортированные янв–май);
            история видна целиком. Пусто → учитывать всё
          </div>
        </Row>
      </Section>

      <Section title="Категории">
        <button onClick={onOpenCategories} style={cardButtonStyle}>
          <div>
            <div style={{ fontSize: 15 }}>Управление категориями</div>
            <div style={{ fontSize: 12, color: theme.colors.sub, marginTop: 4 }}>
              {categoriesCount} категорий · переименование, удаление, цвет, иконка
            </div>
          </div>
          <div style={{ color: theme.colors.sub }}>→</div>
        </button>
      </Section>

      <InvitePanel />

      <ImportTokenSection />

      <Section title="Аккаунт">
        <button
          disabled={busy === 'logout'}
          onClick={async () => {
            setBusy('logout');
            try {
              await api.logout();
            } catch {
              // сессии могло уже не быть — выходим в любом случае
            }
            // очищенная cookie → следующий bootstrap даст 401 → форма логина
            window.location.reload();
          }}
          style={{ ...cardButtonStyle, color: theme.colors.accent }}
        >
          Выйти на этом устройстве
        </button>
      </Section>

      <NotificationsSection />

      <Section title="О приложении">
        <div style={{ fontSize: 12, color: theme.colors.sub, lineHeight: 1.6 }}>
          Бюджет · фаза I · build hash {(import.meta.env as { VITE_GIT_HASH?: string }).VITE_GIT_HASH ?? 'dev'}
          <br />
          Данные хранятся в Cloudflare D1; вход — семейный аккаунт (session-cookie).
        </div>
      </Section>
    </div>
  );
}

/**
 * Токен канала импорта (`POST /api/import-tx`).
 *
 * Сервер хранит только отпечаток, поэтому «посмотреть текущий токен» невозможно
 * в принципе — есть лишь «выпустить новый». Отсюда две обязательные вещи в UX:
 * предупреждение перед ротацией (старый токен умирает мгновенно, а шорткат об
 * этом не узнает и просто перестанет доставлять — молча, без ошибки) и разовый
 * показ, который убирает сам пользователь, а не таймер.
 */
function ImportTokenSection() {
  const toast = useToast();
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [failMsg, setFailMsg] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const info = await api.getImportTokenState();
        if (cancelled) return;
        setConfigured(info.configured);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setFailMsg(e instanceof ApiError ? e.message : 'Не удалось проверить токен');
        setState('failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleIssue() {
    setBusy(true);
    try {
      const res = await api.issueImportToken();
      setToken(res.token);
      setConfigured(true);
      setConfirming(false);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Не удалось выпустить токен', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy(value: string) {
    if (await copyToClipboard(value)) toast.show('Токен скопирован');
    else toast.show('Буфер недоступен — выделите токен вручную', 'error');
  }

  return (
    <Section title="Токен импорта">
      {state === 'loading' && <div style={{ fontSize: 13, color: theme.colors.sub }}>Загрузка…</div>}

      {state === 'failed' && (
        <div style={{ fontSize: 13, color: theme.colors.sub, lineHeight: 1.6 }}>
          Состояние токена сейчас недоступно{failMsg ? ` — ${failMsg}` : ''}.
        </div>
      )}

      {state === 'ready' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name={configured ? 'check' : 'close'} size={15} />
            <div style={{ fontSize: 14 }}>
              {configured ? 'Токен выпущен — канал импорта настроен' : 'Токен ещё не выпущен'}
            </div>
          </div>
          <div style={{ fontSize: 11, color: theme.colors.sub, lineHeight: 1.6 }}>
            Им подписывается шорткат, отправляющий покупки в Инбокс: заголовок
            {' '}<span style={{ color: theme.colors.ink }}>Authorization: Bearer …</span>.
            Сам токен на сервере не лежит — только отпечаток, показать текущий нельзя.
          </div>

          {token && (
            <div
              style={{
                border: `1px solid ${theme.colors.ink}`,
                borderRadius: theme.radius.sm,
                padding: '12px 14px',
                background: theme.colors.bg,
                display: 'grid',
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13.5 }}>Новый токен — показываем один раз</div>
              <div style={{ fontSize: 11, color: theme.colors.sub, lineHeight: 1.6 }}>
                Скопируйте сейчас и вставьте в шорткат. Закроете — восстановить неоткуда,
                останется только выпустить ещё один.
              </div>
              <div
                style={{
                  fontSize: 12,
                  wordBreak: 'break-all',
                  padding: '8px 10px',
                  background: theme.colors.card,
                  border: `1px solid ${theme.colors.line}`,
                  borderRadius: theme.radius.sm,
                  userSelect: 'all',
                }}
              >
                {token}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => void handleCopy(token)} style={{ ...btnStyle, fontSize: 13, padding: '8px 14px' }}>
                  Скопировать
                </button>
                <button onClick={() => setToken(null)} style={subtleBtnStyle}>
                  Скопировал, скрыть
                </button>
              </div>
            </div>
          )}

          {confirming && (
            <div
              style={{
                border: `1px solid ${theme.colors.accent}`,
                borderRadius: theme.radius.sm,
                padding: '12px 14px',
                display: 'grid',
                gap: 10,
              }}
            >
              <div style={{ fontSize: 13, color: theme.colors.accent, lineHeight: 1.6 }}>
                Старый токен перестанет работать сразу. Шорткат на телефоне придётся
                обновить вручную — пока не обновите, покупки просто перестанут доезжать,
                молча и без ошибки.
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  disabled={busy}
                  onClick={() => void handleIssue()}
                  style={{ ...btnStyle, fontSize: 13, padding: '8px 14px', background: theme.colors.accent }}
                >
                  Всё равно выпустить
                </button>
                <button disabled={busy} onClick={() => setConfirming(false)} style={subtleBtnStyle}>
                  Отмена
                </button>
              </div>
            </div>
          )}

          {!confirming && (
            <button
              disabled={busy}
              onClick={() => (configured ? setConfirming(true) : void handleIssue())}
              style={cardButtonStyle}
            >
              <div style={{ fontSize: 14 }}>
                {configured ? 'Выпустить новый токен' : 'Выпустить токен'}
              </div>
              <Icon name="arrowRight" size={15} />
            </button>
          )}
        </>
      )}
    </Section>
  );
}

type PushState = 'loading' | 'unsupported' | 'not-standalone-hint' | 'server-not-configured' | 'off' | 'on';

/** Any valid HH:MM — delivery window is [t, t+15), so any minute start catches exactly one 15-min cron tick. */
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

const DEFAULT_EVENTS: PushEventsSettings = {
  checkin: false,
  checkin_time: '21:00',
  chest_ready: false,
  quest_last_day: false,
  season_finale: false,
  partner_day: false,
  partner_chest: false,
  goal_milestone: false,
};

function NotificationsSection() {
  const toast = useToast();
  const [state, setState] = useState<PushState>('loading');
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const endpointRef = useRef<string | null>(null);
  const [vapidKey, setVapidKey] = useState<string | null>(null);
  const [events, setEvents] = useState<PushEventsSettings>(DEFAULT_EVENTS);
  const [busy, setBusy] = useState(false);
  const isIos = useRef(/iPhone|iPad|iPod/.test(navigator.userAgent)).current;
  const checkinTimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Last checkin_time value successfully persisted to the server. */
  const persistedCheckinTimeRef = useRef<string>(DEFAULT_EVENTS.checkin_time);
  /**
   * Last checkin_time value queued by handleCheckinTime (may not be persisted yet).
   * Used by the unmount flush to fire a PATCH when the component closes before the 400 ms debounce fires.
   */
  const pendingCheckinTimeRef = useRef<string | null>(null);
  /**
   * Full events snapshot captured when the debounce is queued.
   * Lets the unmount flush send the correct events payload even after unmount (state inaccessible).
   */
  const pendingEventsRef = useRef<PushEventsSettings | null>(null);

  // Keep endpointRef in sync so the unmount cleanup can read the current endpoint without stale closure.
  endpointRef.current = endpoint;

  useEffect(() => {
    if (!pushSupported()) {
      setState('unsupported');
      return;
    }
    if (isIos && !isStandalone()) {
      setState('not-standalone-hint');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const sub = await getCurrentSubscription();
        if (cancelled) return;
        const info = await api.getPushInfo(sub?.endpoint);
        if (cancelled) return;
        if (!info.vapid_public_key) {
          setState('server-not-configured');
          return;
        }
        setVapidKey(info.vapid_public_key);
        if (sub) {
          if (info.events === null) {
            // Сервер потерял подписку (удалил по 410 или вручную) — сбрасываем в off;
            // permission уже granted, клик «Включить» пересоздаст без промпта.
            setState('off');
          } else {
            setEndpoint(sub.endpoint);
            setEvents(info.events);
            persistedCheckinTimeRef.current = info.events.checkin_time;
            setState('on');
          }
        } else {
          setState('off');
        }
      } catch {
        if (!cancelled) setState('unsupported');
      }
    })();
    return () => {
      cancelled = true;
      // On unmount: flush any pending debounce immediately instead of silently dropping it.
      // This fixes bug (б): "set time → close Settings within 400 ms → PATCH cancelled → old time reloaded".
      if (checkinTimeDebounceRef.current) {
        clearTimeout(checkinTimeDebounceRef.current);
        checkinTimeDebounceRef.current = null;
        const ep = endpointRef.current;
        const pendingTime = pendingCheckinTimeRef.current;
        const pendingEvents = pendingEventsRef.current;
        if (ep && pendingTime !== null && pendingEvents !== null && pendingTime !== persistedCheckinTimeRef.current) {
          // fire-and-forget: component already unmounted — no setState allowed
          void api.updatePushSubscription({ endpoint: ep, events: pendingEvents });
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleEnable() {
    if (!vapidKey) return;
    setBusy(true);
    try {
      const sub = await subscribePush(vapidKey);
      await api.savePushSubscription({
        ...sub,
        label: deviceLabel(),
        events: DEFAULT_EVENTS,
      });
      setEndpoint(sub.endpoint);
      setEvents(DEFAULT_EVENTS);
      persistedCheckinTimeRef.current = DEFAULT_EVENTS.checkin_time;
      setState('on');
    } catch (e) {
      if (e instanceof Error && e.message === 'permission_denied') {
        toast.show('Разрешение не выдано — можно вернуться позже', 'error');
      } else {
        toast.show(e instanceof ApiError ? e.message : 'Не удалось включить уведомления', 'error');
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDisable() {
    if (!endpoint) return;
    setBusy(true);
    try {
      await unsubscribePush();
      try {
        await api.deletePushSubscription(endpoint);
      } catch (e) {
        // Браузерная отписка уже прошла — переходим в 'off' в любом случае;
        // сообщаем об ошибке сервера тостом, но не блокируем переход.
        toast.show(e instanceof ApiError ? e.message : 'Ошибка удаления на сервере', 'error');
      }
      setEndpoint(null);
      setState('off');
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка отключения', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleEvent(key: keyof Omit<PushEventsSettings, 'checkin_time'>) {
    if (!endpoint) return;
    const next: PushEventsSettings = { ...events, [key]: !events[key] };
    setEvents(next);
    setBusy(true);
    try {
      await api.updatePushSubscription({ endpoint, events: next });
      // Full events object (including current checkin_time) was persisted — keep ref in sync.
      persistedCheckinTimeRef.current = next.checkin_time;
    } catch (e) {
      setEvents(events);
      toast.show(e instanceof ApiError ? e.message : 'Ошибка обновления', 'error');
    } finally {
      setBusy(false);
    }
  }

  function handleCheckinTime(time: string) {
    if (!endpoint) return;
    // Optimistic UI update — any valid HH:MM is accepted (bug (а) fix: relaxed regex, no quarter-hour gate).
    if (!TIME_RE.test(time)) return;
    // Single setEvents call: apply optimistic update AND capture snapshot for the unmount flush.
    setEvents((prev) => {
      const next = { ...prev, checkin_time: time };
      pendingEventsRef.current = next;
      return next;
    });
    if (checkinTimeDebounceRef.current) clearTimeout(checkinTimeDebounceRef.current);
    // Track the pending time so the unmount flush can detect unsaved work.
    pendingCheckinTimeRef.current = time;
    checkinTimeDebounceRef.current = setTimeout(() => {
      pendingCheckinTimeRef.current = null;
      pendingEventsRef.current = null;
      if (!endpoint) return;
      // Compare against the LAST PERSISTED value (not current UI state) to decide
      // whether a PATCH is needed. The optimistic setEvents above already updated
      // the UI state, so comparing against that would always be equal and the PATCH
      // would never fire — the original bug.
      if (time === persistedCheckinTimeRef.current) return;
      let snapshot: PushEventsSettings | undefined;
      setEvents((prev) => {
        snapshot = { ...prev, checkin_time: time };
        return snapshot;
      });
      if (!snapshot) return;
      const committed = snapshot;
      const rollbackTime = persistedCheckinTimeRef.current;
      setBusy(true);
      api.updatePushSubscription({ endpoint, events: committed })
        .then(() => {
          persistedCheckinTimeRef.current = time;
        })
        .catch((e) => {
          setEvents((prev) => ({ ...prev, checkin_time: rollbackTime }));
          toast.show(e instanceof ApiError ? e.message : 'Ошибка обновления', 'error');
        })
        .finally(() => setBusy(false));
    }, 400);
  }

  return (
    <Section title="Уведомления">
      {state === 'loading' && (
        <div style={{ fontSize: 13, color: theme.colors.sub }}>Загрузка…</div>
      )}
      {state === 'unsupported' && (
        <div style={{ fontSize: 13, color: theme.colors.sub }}>
          В этом браузере пуши недоступны.
        </div>
      )}
      {state === 'not-standalone-hint' && (
        <div style={{ fontSize: 13, color: theme.colors.sub, lineHeight: 1.6 }}>
          Установи приложение на экран «Домой» — тогда появятся уведомления.
        </div>
      )}
      {state === 'server-not-configured' && (
        <div style={{ fontSize: 13, color: theme.colors.sub }}>
          Сервер ещё не настроен для пушей.
        </div>
      )}
      {state === 'off' && (
        <button disabled={busy} onClick={handleEnable} style={cardButtonStyle}>
          Включить уведомления на этом устройстве
        </button>
      )}
      {state === 'on' && (
        <>
          <NotifRow
            label="Вечерний чек-ин"
            checked={events.checkin}
            disabled={busy}
            onToggle={() => handleToggleEvent('checkin')}
          >
            {events.checkin && (
              <div style={{ marginTop: 8 }}>
                <input
                  type="time"
                  step={60}
                  value={events.checkin_time}
                  onChange={(e) => void handleCheckinTime(e.target.value)}
                  style={{ ...inputStyle, width: 110 }}
                />
                <div style={{ fontSize: 11, color: theme.colors.sub, marginTop: 4 }}>
                  Тихие часы: 22:00–09:00 — в них ничего не приходит
                </div>
              </div>
            )}
          </NotifRow>

          <NotifRow
            label="Сундук недели (вс утром)"
            checked={events.chest_ready}
            disabled={busy}
            onToggle={() => handleToggleEvent('chest_ready')}
          />

          <NotifRow
            label="Квест недели (сб утром)"
            checked={events.quest_last_day}
            disabled={busy}
            onToggle={() => handleToggleEvent('quest_last_day')}
          />

          <NotifRow
            label="Финал сезона (1-го числа)"
            checked={events.season_finale}
            disabled={busy}
            onToggle={() => handleToggleEvent('season_finale')}
          />

          <div style={{ fontSize: 11, color: theme.colors.sub, marginTop: 12, marginBottom: 2, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Партнёрские
          </div>

          <NotifRow
            label="Партнёр записал день"
            checked={events.partner_day}
            disabled={busy}
            onToggle={() => handleToggleEvent('partner_day')}
          />

          <NotifRow
            label="Партнёр открыл сундук или сезон"
            checked={events.partner_chest}
            disabled={busy}
            onToggle={() => handleToggleEvent('partner_chest')}
          />

          <NotifRow
            label="Вехи целей — 50% и 100%"
            checked={events.goal_milestone}
            disabled={busy}
            onToggle={() => handleToggleEvent('goal_milestone')}
          />

          <button
            disabled={busy}
            onClick={handleDisable}
            style={{ ...cardButtonStyle, color: theme.colors.sub, fontSize: 13 }}
          >
            Отключить на этом устройстве
          </button>
        </>
      )}
    </Section>
  );
}

function NotifRow({
  label,
  checked,
  disabled,
  onToggle,
  children,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 14, color: theme.colors.ink }}>{label}</div>
        <button
          disabled={disabled}
          onClick={onToggle}
          style={{
            padding: '5px 12px',
            fontSize: 12,
            fontFamily: theme.font,
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.sm,
            background: checked ? theme.colors.ink : theme.colors.bg,
            color: checked ? theme.colors.paper : theme.colors.sub,
            cursor: disabled ? 'default' : 'pointer',
            minWidth: 60,
          }}
        >
          {checked ? 'Вкл' : 'Выкл'}
        </button>
      </div>
      {children}
    </div>
  );
}

function Header({ onClose }: { onClose: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
      <button
        onClick={onClose}
        style={{
          background: 'transparent',
          border: 'none',
          color: theme.colors.sub,
          fontSize: 18,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        ←
      </button>
      <div>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 2,
            textTransform: 'uppercase',
            color: theme.colors.sub,
          }}
        >
          Настройки
        </div>
        <h1 style={{ margin: '4px 0 0', fontSize: 24, fontWeight: 400 }}>Настроить</h1>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: theme.colors.sub,
          marginBottom: 8,
          paddingLeft: 4,
        }}
      >
        {title}
      </div>
      <div
        style={{
          background: theme.colors.card,
          border: `1px solid ${theme.colors.line}`,
          borderRadius: theme.radius.lg,
          padding: 16,
          display: 'grid',
          gap: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: theme.colors.sub, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  flex: 1,
  padding: '9px 12px',
  fontSize: 15,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  background: theme.colors.bg,
  fontFamily: theme.font,
  outline: 'none',
  boxSizing: 'border-box',
};

const btnStyle: React.CSSProperties = {
  padding: '9px 14px',
  background: theme.colors.ink,
  color: theme.colors.paper,
  border: 'none',
  borderRadius: theme.radius.sm,
  fontFamily: theme.font,
  fontSize: 14,
  cursor: 'pointer',
};

const subtleBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontFamily: theme.font,
  background: 'transparent',
  color: theme.colors.sub,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  cursor: 'pointer',
};

const cardButtonStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  background: theme.colors.bg,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.md,
  padding: '12px 14px',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: theme.font,
  color: theme.colors.ink,
};
